import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { Village } from '../types';
import { Card, Input, Button, Badge } from './ui';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 3958.8; // Radius of Earth in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

export const DiscoverHubMap: React.FC = () => {
   const { currentUser } = useAuth();
   const navigate = useNavigate();

   const [publicVillages, setPublicVillages] = useState<Village[]>([]);
   const [searchHubQuery, setSearchHubQuery] = useState('');
   const [isExpanded, setIsExpanded] = useState(false);
   const [isSearchingNearby, setIsSearchingNearby] = useState(false);
   const [nearbyResults, setNearbyResults] = useState<{hub: Village, distance: number}[] | null>(null);

   const [discoverSuggestions, setDiscoverSuggestions] = useState<any[]>([]);
   const [isSearchingLocation, setIsSearchingLocation] = useState(false);
   const [discoverCoordinates, setDiscoverCoordinates] = useState<[number, number] | null>(null);

   useEffect(() => {
       if (!currentUser) return;
       const qPub = query(collection(db, 'villages'), where('isPublic', '==', true));
       const unsub = onSnapshot(qPub, snap => {
           setPublicVillages(snap.docs.map(d => ({ id: d.id, ...d.data() } as Village)));
       });
       return () => unsub();
   }, [currentUser]);

   useEffect(() => {
    if (!searchHubQuery.trim() || discoverCoordinates) {
        setDiscoverSuggestions([]);
        return;
    }
    const delay = setTimeout(async () => {
        setIsSearchingLocation(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&q=${encodeURIComponent(searchHubQuery)}`);
            const data = await res.json();
            setDiscoverSuggestions(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSearchingLocation(false);
        }
    }, 400);
    return () => clearTimeout(delay);
  }, [searchHubQuery, discoverCoordinates]);

  useEffect(() => {
     if (!searchHubQuery.trim()) { setNearbyResults(null); return; }
     
     if (discoverCoordinates) {
         const [sLat, sLon] = discoverCoordinates;
         const withDist = publicVillages.map(v => {
             if (!v.coordinates) return { hub: v, distance: 999999 };
             return { hub: v, distance: calculateDistance(sLat, sLon, v.coordinates[0], v.coordinates[1]) };
         }).sort((a,b) => a.distance - b.distance);
         setNearbyResults(withDist.slice(0, 3));
         setIsSearchingNearby(false);
         return;
     }

     const delay = setTimeout(async () => {
         const textMatch = publicVillages.filter(v => 
             v.name.toLowerCase().includes(searchHubQuery.toLowerCase()) || 
             v.location.toLowerCase().includes(searchHubQuery.toLowerCase())
         );
         if (textMatch.length === 0 && searchHubQuery.length > 2) {
             setIsSearchingNearby(true);
             try {
                 const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&q=${encodeURIComponent(searchHubQuery)}`);
                 const data = await res.json();
                 if (data && data.length > 0) {
                     const sLat = parseFloat(data[0].lat);
                     const sLon = parseFloat(data[0].lon);
                     const withDist = publicVillages.map(v => {
                         if (!v.coordinates) return { hub: v, distance: 999999 };
                         return { hub: v, distance: calculateDistance(sLat, sLon, v.coordinates[0], v.coordinates[1]) };
                     }).sort((a,b) => a.distance - b.distance);
                     setNearbyResults(withDist.slice(0, 3));
                 } else {
                     setNearbyResults([]);
                 }
             } catch (e) {
                 setNearbyResults([]);
             } finally {
                 setIsSearchingNearby(false);
             }
         } else {
             setNearbyResults(null);
         }
     }, 1000);
     return () => clearTimeout(delay);
  }, [searchHubQuery, discoverCoordinates, publicVillages]);

  const handleJoinVillage = async (villageId: string) => {
    if (!currentUser) return;
    try {
        const vRef = doc(db, 'villages', villageId);
        await updateDoc(vRef, { members: arrayUnion(currentUser.uid) });
        navigate('/messages'); 
    } catch(e) {
        console.error(e);
    }
  };

  const textMatchHubs = searchHubQuery 
      ? publicVillages.filter(v => v.name.toLowerCase().includes(searchHubQuery.toLowerCase()) || v.location.toLowerCase().includes(searchHubQuery.toLowerCase()))
      : publicVillages;
  const displayedHubs = nearbyResults ? nearbyResults.map(r => r.hub) : textMatchHubs;

  const displayHubsList = nearbyResults 
      ? nearbyResults
      : textMatchHubs.map(hub => ({ hub, distance: null as number | null }));

  const showMap = isExpanded || searchHubQuery.trim().length > 0;

  return (
        <Card className={`overflow-hidden border-0 shadow-sm col-span-1 md:col-span-12 ring-1 ring-slate-200 transition-all relative group/hub ${!showMap ? 'cursor-pointer hover:border-indigo-300' : 'mt-8'}`} onClick={() => !showMap && setIsExpanded(true)}>
            {showMap && (
                <button 
                    onClick={(e) => { e.stopPropagation(); setIsExpanded(false); setSearchHubQuery(''); }}
                    className="absolute top-6 right-6 z-20 text-slate-400 hover:text-rose-500 transition-colors bg-white/50 backdrop-blur-sm rounded-full p-2 border border-slate-200 shadow-sm opacity-100 md:opacity-0 md:group-hover/hub:opacity-100"
                    title="Hide Local Hubs"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                </button>
            )}
            <div className={`p-6 bg-slate-50 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-colors ${!showMap ? 'group-hover/hub:bg-indigo-50/30' : 'border-b border-slate-200'}`}>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl shadow-sm shrink-0">
                        🗺️
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 font-heading">Discover Local Hubs</h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Find peers in your area ({publicVillages.length} Hubs Nationwide)</p>
                    </div>
                </div>
                
                <div className="w-full md:w-96 relative">
                    <Input 
                        label=""
                        placeholder="Search town, city or region..." 
                        value={searchHubQuery} 
                        onChange={(e) => {
                            setSearchHubQuery(e.target.value);
                            setDiscoverCoordinates(null);
                        }} 
                        className="mb-0 text-sm w-full" 
                    />
                    {isSearchingLocation && <div className="absolute right-4 top-3 text-[10px] text-slate-400 font-bold animate-pulse">Searching...</div>}
                    {discoverSuggestions.length > 0 && !discoverCoordinates && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto custom-scrollbar">
                            {discoverSuggestions.map((loc, i) => (
                                <button 
                                    key={i} 
                                    type="button"
                                    onClick={() => {
                                        setSearchHubQuery(loc.display_name);
                                        setDiscoverCoordinates([parseFloat(loc.lat), parseFloat(loc.lon)]);
                                        setDiscoverSuggestions([]);
                                    }}
                                    className="w-full text-left px-4 py-3 text-[11px] font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 border-b border-slate-100 last:border-0"
                                >
                                    {loc.display_name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {!showMap && (
                    <div className="flex items-center gap-2 text-[10px] font-bold text-indigo-600 uppercase tracking-widest bg-indigo-50 px-3 py-1.5 rounded-lg whitespace-nowrap shrink-0 opacity-100 md:opacity-80 md:group-hover/hub:opacity-100 transition-opacity">
                        <span>Tap to open map</span>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                )}
            </div>

            {showMap && (
            <div className="grid grid-cols-1 md:grid-cols-2 animate-in fade-in slide-in-from-top-4 duration-300">
                <div className="h-[300px] md:h-[450px] z-0 relative bg-slate-100 border-r border-slate-200">
                    <MapContainer 
                        key={displayedHubs.find(v => v.coordinates)?.id || 'default'} 
                        center={displayedHubs.find(v => v.coordinates)?.coordinates as [number, number] || [54.5, -2.5] as [number, number]} 
                        zoom={nearbyResults ? 10 : (displayedHubs.length <= 1 ? 10 : 6)} 
                        scrollWheelZoom={false} 
                        style={{ height: '100%', width: '100%', minHeight: '300px', zIndex: 0 }}
                    >
                        <TileLayer
                            attribution='&copy; OpenStreetMap'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {displayedHubs.map(v => v.coordinates && (
                            <Marker key={'map-'+v.id} position={v.coordinates as [number, number]}>
                                <Popup>
                                    <b className="font-heading">{v.name}</b><br/>
                                    <span className="text-xs text-slate-500">{v.location}</span>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>

                <div className="flex flex-col h-[350px] md:h-[450px]">
                    {(isSearchingNearby || nearbyResults) && (
                        <div className="p-3 px-6 bg-amber-50/50 border-b border-amber-100/50 text-center flex-shrink-0">
                            {isSearchingNearby && <p className="text-[10px] font-bold text-amber-600 animate-pulse">Scanning geographic region...</p>}
                            {nearbyResults && !isSearchingNearby && <p className="text-[10px] font-bold text-amber-700">No exact match. Showing closest hubs.</p>}
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-slate-100 bg-white p-4">
                        {displayHubsList.length === 0 && !isSearchingNearby ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-8">
                                <span className="text-5xl grayscale opacity-30 mb-4 tracking-tighter shadow-sm border border-slate-100 rounded-full w-20 h-20 flex items-center justify-center">⛺</span>
                                <h3 className="font-bold text-slate-700 mb-2">No active hubs found here</h3>
                                <p className="text-xs text-slate-500 mb-6 max-w-[250px]">There are no public hubs meeting this exact search criteria.</p>
                                <Button onClick={() => navigate('/messages?createHub=true')} variant="primary" className="w-full sm:w-auto shadow-md">
                                    Form a Local Hub
                                </Button>
                            </div>
                        ) : (
                            <div className="pb-4 flex flex-col h-full">
                                {displayHubsList.map(({ hub: v, distance }) => {
                                    const isMember = currentUser && v.members.includes(currentUser.uid);
                                    return (
                                    <div key={v.id} className="bg-white p-5 rounded-xl flex items-center justify-between gap-4 hover:bg-slate-50 transition-all border border-slate-100 group mb-3 shadow-sm flex-shrink-0">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center text-3xl shadow-sm border border-indigo-100 flex-shrink-0 group-hover:scale-110 transition-transform">{v.shieldIcon || '🛡️'}</div>
                                            <div className="min-w-0 flex flex-col gap-0.5">
                                                <p className="text-base font-black text-slate-900 tracking-tight leading-none truncate">{v.name}</p>
                                                <p className="text-[11px] text-slate-600 font-medium truncate mt-1 italic">{v.description}</p>
                                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                                    <span className="text-[9px] bg-slate-100 text-slate-600 px-2 py-1 rounded font-bold tracking-widest uppercase">
                                                        📍 {v.location.split(',').slice(0, 2).join(',')}
                                                    </span>
                                                    {distance !== null && (
                                                        <span className="text-[9px] bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-bold tracking-widest uppercase shadow-sm">
                                                            {distance.toFixed(1)} miles away
                                                        </span>
                                                    )}
                                                    <span className="text-[9px] text-slate-500 font-bold tracking-widest uppercase">
                                                        • {v.members.length} member{v.members.length !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {isMember ? (
                                            <button disabled className="px-5 py-2.5 bg-slate-100 border border-slate-200 text-slate-400 text-[10px] font-black uppercase tracking-widest rounded-lg cursor-not-allowed whitespace-nowrap shadow-none">Joined</button>
                                        ) : (
                                            <button onClick={() => handleJoinVillage(v.id!)} className="px-5 py-2.5 bg-indigo-600 border border-indigo-700 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-indigo-700 hover:shadow-lg transition-all whitespace-nowrap shadow-sm">Join Hub</button>
                                        )}
                                    </div>
                                    );
                                })}
                                
                                <div className="mt-4 pt-4 border-t border-slate-100 w-full flex justify-center pb-2">
                                    <button onClick={() => navigate('/messages?createHub=true')} className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-widest rounded-lg hover:bg-slate-100 hover:text-slate-900 transition-colors shadow-sm">
                                        <span>+</span> Form a Local Hub Instead
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            )}
        </Card>
   );
};
